import React, { useEffect } from "react";
import {
  CreditCard,
  Calendar,
  Users,
  CheckCircle,
  X,
  Activity,
  TrendingUp,
  CheckCheck,
} from "lucide-react";
import translations from "../../../constants/translations";
import Mo3tiButton from "../../layout/Mo3tiButton";
import StepSelectPeriod from "./StepSelectPeriod";
import StepChooseEmployees from "./StepChooseEmployees";
import StepReviewProcess from "./StepReviewProcess";
import useProcessPayroll from "../../../hooks/useProcessPayroll";

const ProcessPayrollModal = ({
  onClose,
  onProcessComplete,
  payrollData,
  selectedEmployees,
  setSelectedEmployees,
  selectedMonth,
  setSelectedMonth,
  theme,
  language,
  API_BASE_URL,
  clientName,
  showAlert,
  processStep,
  setProcessStep,
}) => {
  const currentTheme = {
    colors: {
      primary: "#2e7cba",
      success: "#10b981",
      textPrimary: theme === "dark" ? "#ffffff" : "#1a1a1a",
      textSecondary: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
    },
  };
  const t = (key) => translations[language][key] || key;

  const modalStyles = {
    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.7)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 3000,
      padding: "20px",
      overflow: "hidden",
    },
    modalContent: {
      background: theme === "dark" ? "#1e293b" : "#ffffff",
      border: theme === "dark" ? "1px solid #334155" : "1px solid #e2e8f0",
      borderRadius: "16px",
      width: "100%",
      maxWidth: "1000px",
      margin: "0 auto",
      maxHeight: "90vh",
      display: "flex",
      flexDirection: "column",
      boxShadow: "none",
      overflow: "hidden",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "24px",
      borderBottom: theme === "dark" ? "1px solid #334155" : "1px solid #e2e8f0",
    },
    body: {
      padding: "24px",
      overflowY: "auto",
      flex: 1,
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      WebkitOverflowScrolling: "touch", // للـ iOS
    },
    footer: {
      padding: "24px",
      borderTop: theme === "dark" ? "1px solid #334155" : "1px solid #e2e8f0",
    },
    title: {
      color: theme === "dark" ? "#ffffff" : "#1a1a1a",
      fontSize: "24px",
      fontWeight: "700",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    formSection: {
      background:
        theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
      borderRadius: "20px",
      padding: "32px",
      border:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.1)"
          : "2px solid rgba(0,0,0,0.05)",
      marginBottom: "24px",
    },
    sectionTitle: {
      color: theme === "dark" ? "#ffffff" : "#1a1a1a",
      fontSize: "24px",
      fontWeight: "700",
      marginBottom: "24px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "24px",
    },
    formGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    formGroupWithMargin: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      marginTop: "20px",
    },
    label: {
      color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
      fontSize: "14px",
      fontWeight: "600",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    input: {
      background:
        theme === "dark"
          ? "rgba(30, 41, 59, 0.8)"
          : "rgba(255, 255, 255, 0.95)",
      border:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.1)"
          : "2px solid rgba(0,0,0,0.1)",
      borderRadius: "12px",
      padding: "16px",
      color: theme === "dark" ? "#ffffff" : "#1a1a1a",
      fontSize: "16px",
      fontWeight: "500",
      outline: "none",
    },
    checkboxGroup: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    stepIndicator: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "12px",
      marginBottom: "24px",
      padding: "16px",
      background: theme === "dark" ? "#0f172a" : "#f8fafc",
      borderRadius: "12px",
      flexWrap: "wrap",
    },
    step: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 16px",
      borderRadius: "8px",
      fontSize: "13px",
      fontWeight: "600",
      color: theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
    },
    activeStep: {
      background: "#2e7cba",
      color: "#ffffff",
    },
    stepNumber: {
      width: "24px",
      height: "24px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "12px",
      fontWeight: "700",
      background: theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
    },
    activeStepNumber: {
      background: "#ffffff",
      color: "#2e7cba",
    },
    employeeList: {
      maxHeight: "400px",
      overflowY: "auto",
      padding: "16px",
      background:
        theme === "dark" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.5)",
      borderRadius: "12px",
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      WebkitOverflowScrolling: "touch", // للـ iOS
    },
    employeeItem: {
      padding: "16px",
      background:
        theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.9)",
      borderRadius: "12px",
      marginBottom: "12px",
      display: "flex",
      alignItems: "center",
      gap: "16px",
      cursor: "pointer",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.1)",
    },
    previewCard: {
      background:
        theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
      borderRadius: "16px",
      padding: "24px",
      marginBottom: "20px",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.1)",
    },
    previewTotal: {
      marginTop: "20px",
      padding: "20px",
      background: "#2e7cba",
      color: "#ffffff",
      borderRadius: "12px",
      fontSize: "24px",
      fontWeight: "800",
      textAlign: "center",
    },
    actionButtons: {
      display: "flex",
      gap: "16px",
      justifyContent: "flex-end",
    },
  };

  const {
    companyConfig,
    cutoffDay,
    companies,
    companyConfigsById,
    companiesLoading,
    companiesError,
    formData,
    setFormData,
    previewData,
    selectedAll,
    setSelectedAll,
    processingPayroll,
    processingStatus,
    handleInputChange,
    handleEmployeeSelection,
    handleSelectAll,
    calculatePreview,
    handleProcessPayroll,
  } = useProcessPayroll({
    isOpen: true,
    onClose,
    selectedMonth,
    onProcessComplete,
    setSelectedMonth,
    showAlert,
    t,
    language,
    selectedEmployees,
    setSelectedEmployees,
    payrollData,
    clientName,
  });

  useEffect(() => {
    if (processStep === 3 && selectedEmployees.length > 0) {
      calculatePreview();
    }
  }, [processStep, selectedEmployees]);

  const steps = [
    { number: 1, title: t("selectPeriod"), icon: Calendar },
    { number: 2, title: t("chooseEmployees"), icon: Users },
    { number: 3, title: t("reviewProcess"), icon: CheckCircle },
  ];

  const renderStepContent = () => {
    switch (processStep) {
      case 1:
        return (
          <StepSelectPeriod
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
            modalStyles={modalStyles}
            t={t}
            theme={theme}
            language={language}
            companies={companies}
            companyConfigsById={companyConfigsById}
            companyConfig={companyConfig}
            companiesLoading={companiesLoading}
            companiesError={companiesError}
          />
        );

      case 2:
        return (
          <StepChooseEmployees
            payrollData={payrollData}
            selectedEmployees={selectedEmployees}
            setSelectedEmployees={setSelectedEmployees}
            handleEmployeeSelection={handleEmployeeSelection}
            setSelectedAll={setSelectedAll}
            selectedCompanyIds={formData.companyIds || []}
            modalStyles={modalStyles}
            currentTheme={currentTheme}
            t={t}
          />
        );

      case 3:
        return (
          <StepReviewProcess
            previewData={previewData}
            selectedEmployees={selectedEmployees}
            processingPayroll={processingPayroll}
            modalStyles={modalStyles}
            currentTheme={currentTheme}
            t={t}
          />
        );

      default:
        return null;
    }
  };

  const handleNext = () => {
    if (processStep === 3) {
      handleProcessPayroll(false);
    } else {
      setProcessStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setProcessStep((prev) => prev - 1);
  };

  // ── Step 1 validity ──────────────────────────────────────────────────────
  // Multi-company flow: at least one company selected, and every selected
  // company has a period with month/year set.
  // Falls back to the legacy single-period shape when no companies were
  // selected (e.g. companies list hasn't loaded / single-company clients).
  const hasValidMultiCompanySelection = () => {
    const companyIds = formData.companyIds || [];
    if (companyIds.length === 0) return false;

    const periods = formData.periodsByCompany || {};
    return companyIds.every((id) => {
      const period = periods[id];
      return period && period.month && period.year;
    });
  };

  const canProceed = () => {
    switch (processStep) {
      case 1:
        return (
          hasValidMultiCompanySelection() ||
          (formData.payrollPeriod.month && formData.payrollPeriod.year)
        );
      case 2:
        return selectedEmployees.length > 0;
      case 3:
        return previewData && selectedEmployees.length > 0;
      default:
        return false;
    }
  };

  return (
    <div
      style={modalStyles.modal}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <style>{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <div style={modalStyles.modalContent}>
        <div style={modalStyles.header}>
          <h1 style={modalStyles.title}>
            <CreditCard size={24} />
            {t("processNewPayroll")}
          </h1>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={24} />
          </button>
        </div>

        <div style={modalStyles.body} className="no-scrollbar">
          <div style={modalStyles.stepIndicator}>
            {steps.map((step) => (
              <div
                key={step.number}
                style={{
                  ...modalStyles.step,
                  ...(processStep >= step.number ? modalStyles.activeStep : {}),
                }}
              >
                <div
                  style={{
                    ...modalStyles.stepNumber,
                    ...(processStep >= step.number
                      ? modalStyles.activeStepNumber
                      : {}),
                  }}
                >
                  {step.number}
                </div>
                {step.title}
              </div>
            ))}
          </div>

          {renderStepContent()}
        </div>

        <div style={modalStyles.footer}>
          <div style={modalStyles.actionButtons}>
            {processStep > 1 && (
              <Mo3tiButton
                variant="secondary"
                onClick={handleBack}
                disabled={processingPayroll}
              >
                {t("back")}
              </Mo3tiButton>
            )}

            {processStep === 3 ? (
              <>
                <Mo3tiButton
                  variant="primary"
                  onClick={() => handleProcessPayroll(false)}
                  disabled={!canProceed() || processingPayroll}
                  icon={processingPayroll ? Activity : CheckCircle}
                >
                  {processingPayroll ? t("processing") : t("processPayrollBtn")}
                </Mo3tiButton>
                <Mo3tiButton
                  variant="primary"
                  onClick={() => handleProcessPayroll(true)}
                  disabled={!canProceed() || processingPayroll}
                  icon={processingPayroll ? Activity : CheckCheck}
                  style={{ backgroundColor: currentTheme.colors.success, borderColor: currentTheme.colors.success, color: "#ffffff" }}
                >
                  {processingPayroll ? t("processing") : "Process & Mark Paid"}
                </Mo3tiButton>
              </>
            ) : (
              <Mo3tiButton
                variant="primary"
                onClick={handleNext}
                disabled={!canProceed() || processingPayroll}
                icon={TrendingUp}
              >
                {t("nextStep")}
              </Mo3tiButton>
            )}
          </div>

          {processingPayroll && processingStatus && (
            <div
              style={{
                marginTop: "10px",
                color: currentTheme.colors.textSecondary,
                fontSize: "13px",
                textAlign: "right",
              }}
            >
              {processingStatus}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcessPayrollModal;